import React, { useState, useEffect } from 'react';
import { ArrowLeft, Lock, Gift, AlertCircle } from 'lucide-react';
import StripeProvider from '../payment/StripeProvider';
import StripeCheckoutForm from './StripeCheckoutForm';
import { ref, push, set, get } from 'firebase/database';
import { database } from '../../firebase';
import useAuth from '../../hooks/useAuth';
import giftCardService from '../services/giftCardServices';

const GiftCardCheckout = ({ 
  giftCardDetails,
  onBack, 
  onSuccess
}) => {
  const { user } = useAuth();
  const [clientSecret, setClientSecret] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [dbError, setDbError] = useState(null);
  
  // Calculate and return totals for display
  const calculateTotals = () => {
    const amount = parseFloat(giftCardDetails.amount);
    const taxRate = 0; // Gift cards are typically not taxed
    const tax = amount * taxRate;
    const total = amount + tax;
    
    return {
      subtotal: amount.toFixed(2),
      tax: tax.toFixed(2),
      total: total.toFixed(2)
    };
  };
  
  // Fetch client secret when component loads
  useEffect(() => {
    fetchClientSecret();
  }, []);
  
  // Function to fetch client secret
// Function to fetch client secret
const fetchClientSecret = async () => {
  try {
    setPaymentLoading(true);
    setPaymentError(null);
    
    const totals = calculateTotals();
    const orderData = prepareOrderData();
    
    const response = await fetch('/api/create-payment-intent', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        amount: parseFloat(totals.total),
        metadata: orderData
      }),
    });
    
    // Check for non-JSON responses
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      throw new Error(`Server returned non-JSON response: ${await response.text()}`);
    }
    
    const data = await response.json();
    
    if (response.ok) {
      setClientSecret(data.clientSecret);
    } else {
      setPaymentError(data.error || 'Unable to set up payment. Please try again.');
    }
  } catch (error) {
    console.error('Network error in fetchClientSecret:', error);
    setPaymentError('Network error. Please check your connection and try again. Make sure the API route is properly configured.');
  } finally {
    setPaymentLoading(false);
  }
};
  // Prepare order data for Stripe as strings
  const prepareOrderData = () => {
    const orderData = {
      id: `gift-card-${Date.now()}`,
      type: 'gift_card',
      amount: giftCardDetails.amount.toString(),
      recipientName: giftCardDetails.recipientName || '',
      recipientEmail: giftCardDetails.recipientEmail || '',
      senderName: giftCardDetails.senderName || '',
      message: giftCardDetails.message || '',
    };
    
    return orderData;
  };
  
  // Function to save gift card to Firebase after successful payment
  const saveGiftCardToFirebase = async (paymentIntent) => {
    console.log("Payment intent received:", paymentIntent);
    console.log("Current user state:", user);
    
    try {
      // Create the gift card data object
      const code = giftCardService.generateCode();
      const nowISOString = new Date().toISOString();
      const expirationDate = new Date();
      expirationDate.setFullYear(expirationDate.getFullYear() + 1);
      
      const giftCardData = {
        code,
        amount: parseFloat(giftCardDetails.amount),
        balance: parseFloat(giftCardDetails.amount),
        status: 'active',
        recipientName: giftCardDetails.recipientName || '',
        recipientEmail: giftCardDetails.recipientEmail || '',
        senderName: giftCardDetails.senderName || (user ? user.displayName : ''),
        message: giftCardDetails.message || '',
        createdAt: nowISOString,
        expiresAt: expirationDate.toISOString(),
        transactions: [{
          type: 'creation',
          amount: parseFloat(giftCardDetails.amount),
          date: nowISOString
        }],
        payment: {
          status: 'paid',
          date: nowISOString,
          method: 'credit_card',
          last4: paymentIntent?.payment_method_details?.card?.last4 || '****',
          paymentIntentId: paymentIntent?.id || null
        }
      };
      
      console.log("Preparing to save gift card:", giftCardData);
      
      // Save gift card in global gift cards collection
      const newGiftCardRef = push(ref(database, 'giftCards'));
      const giftCardId = newGiftCardRef.key;
      console.log("Generated new gift card ID:", giftCardId);
      
      // Save to main gift cards collection
      await set(newGiftCardRef, giftCardData);
      console.log("Gift card saved successfully to main collection");
      
      // Also save reference in user's account if logged in
      if (user) {
        console.log("Adding gift card to user account:", user.uid);
        
        const userGiftCardData = {
          id: giftCardId,
          type: 'purchased',
          amount: parseFloat(giftCardDetails.amount),
          recipientName: giftCardDetails.recipientName || '',
          createdAt: nowISOString,
        };
        
        await set(ref(database, `users/${user.uid}/giftCards/${giftCardId}`), userGiftCardData);
        console.log("Gift card reference saved to user account");
      }
      
      // Return the gift card data with ID
      return {
        id: giftCardId,
        ...giftCardData
      };
      
    } catch (error) {
      console.error('Error saving gift card to Firebase:', error);
      setDbError("Failed to create gift card. Please try again or contact support.");
      throw error;
    }
  };
  
  // Function to handle successful payment
  const handlePaymentSuccess = async (paymentIntent) => {
    try {
      console.log("Payment success handler called with intent:", paymentIntent?.id);
      
      // Clear any previous errors
      setDbError(null);
      
      // Save the gift card to Firebase
      const savedGiftCard = await saveGiftCardToFirebase(paymentIntent);
      
      if (savedGiftCard) {
        console.log("Gift card data saved successfully:", savedGiftCard);
        
        // Send email to recipient if email is provided
        if (giftCardDetails.recipientEmail) {
          try {
            console.log("Sending gift card email to:", giftCardDetails.recipientEmail);
            
            await fetch('/api/send-gift-card-email', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({ 
                giftCard: savedGiftCard,
                senderId: user ? user.uid : null
              }),
            });
            
            console.log("Gift card email sent successfully");
          } catch (emailError) {
            console.error('Error sending gift card email:', emailError);
            // Continue with success flow even if email fails
          }
        }
        
        // Call the onSuccess callback with the saved gift card data
        onSuccess(savedGiftCard);
      } else {
        // Still call onSuccess but with a warning
        console.error("Gift card not properly saved but payment was successful");
        alert('Your payment was successful, but there was an issue creating your gift card. Please contact support.');
        onSuccess();
      }
    } catch (error) {
      console.error('Error in payment success handler:', error);
      alert('Payment successful, but there was an error processing your gift card. Please contact support.');
      onSuccess();
    }
  };
  
  const totals = calculateTotals();

  // Render Stripe payment form or loading/error state
  const renderStripePaymentForm = () => {
    if (paymentLoading) {
      return (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-blue-600 flex items-center">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600 mr-3"></div>
          <span>Preparing payment form...</span>
        </div>
      );
    }
    
    if (paymentError) {
      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
          <p className="font-medium mb-1">Payment setup error</p>
          <p>{paymentError}</p>
          <button 
            onClick={fetchClientSecret}
            className="mt-3 text-sm text-blue-600 hover:text-blue-800"
          >
            Try again
          </button>
        </div>
      );
    }
    
    if (dbError) {
      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600 flex items-start">
          <AlertCircle className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium mb-1">Database Error</p>
            <p>{dbError}</p>
            <p className="mt-2 text-sm">This usually indicates an issue with your account permissions or network connection.</p>
          </div>
        </div>
      );
    }
    
    if (!clientSecret) {
      return (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-gray-600 flex items-center">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-600 mr-3"></div>
          <span>Initializing payment system...</span>
        </div>
      );
    }
    
    // Only render StripeProvider when we have a clientSecret
    return (
      <StripeProvider clientSecret={clientSecret}>
        <StripeCheckoutForm 
          onSuccess={handlePaymentSuccess}
          onBack={onBack}
          serviceDetails={{
            title: `Gift Card - $${giftCardDetails.amount}`,
            price: parseFloat(giftCardDetails.amount),
            description: 'Auto Detailing Gift Card'
          }}
          customerInfo={{
            name: giftCardDetails.senderName || '',
            email: user ? user.email : ''
          }}
          paymentDetails={{
            subtotal: parseFloat(totals.subtotal),
            tax: parseFloat(totals.tax),
            total: parseFloat(totals.total)
          }}
        />
      </StripeProvider>
    );
  };
  
  return (
    <div className="bg-white rounded-xl shadow-md overflow-hidden p-6 mb-6">
      {/* Gift Card Summary */}
      <div className="mb-8 border-b border-gray-200 pb-6">
        <h2 className="text-2xl font-bold mb-4">Gift Card Summary</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-2">Gift Amount</h3>
            <p className="text-2xl font-bold text-blue-600">${giftCardDetails.amount}</p>
          </div>
          
          <div>
            <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-2">Recipient</h3>
            <p className="font-medium">{giftCardDetails.recipientName || 'Not specified'}</p>
            {giftCardDetails.recipientEmail && (
              <p className="text-gray-600 text-sm">{giftCardDetails.recipientEmail}</p>
            )}
          </div>
          
          {giftCardDetails.message && (
            <div className="md:col-span-2">
              <h3 className="text-sm uppercase tracking-wider text-gray-500 mb-2">Gift Message</h3>
              <p className="text-gray-700 bg-gray-50 p-3 rounded-lg italic">"{giftCardDetails.message}"</p>
            </div>
          )}
        </div>
        
        <div className="mt-6 text-gray-600 flex items-start">
          <Gift className="h-5 w-5 mr-2 mt-0.5 text-blue-500" />
          <p>This gift card will be valid for one year from purchase date and can be applied to any of our detailing services.</p>
        </div>
      </div>
      
      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Payment Form */}
        <div className="lg:col-span-2">
          <h3 className="text-lg font-semibold mb-4">Payment Information</h3>
          
          {/* Stripe Payment Form */}
          {renderStripePaymentForm()}
        </div>
        
        {/* Order Summary */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Order Summary</h3>
          
          <div className="bg-gray-50 rounded-lg p-4 mb-4">
            <div className="border-b border-gray-200 pb-4 mb-4">
              <div className="flex justify-between mb-2">
                <span className="text-gray-600">Gift Card</span>
                <span className="font-medium">${totals.subtotal}</span>
              </div>
              
              {parseFloat(totals.tax) > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-500">Tax</span>
                  <span>${totals.tax}</span>
                </div>
              )}
            </div>
            
            <div className="flex justify-between font-semibold text-lg">
              <span>Total</span>
              <span>${totals.total}</span>
            </div>
          </div>
          
          <div className="text-sm text-gray-500 space-y-2">
            <p className="flex items-start">
              <Lock className="h-4 w-4 mr-2 mt-0.5 text-gray-400" />
              Your payment information is encrypted and secure.
            </p>
            <p>By completing your purchase, you agree to our Terms of Service and Privacy Policy.</p>
          </div>
          
          {user ? (
            <div className="mt-4 p-3 bg-green-50 border border-green-100 rounded-lg text-green-700 text-sm flex items-start">
              <Gift className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
              <p>You're logged in as {user.email}. Your gift card will be saved to your account automatically.</p>
            </div>
          ) : (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-100 rounded-lg text-yellow-700 text-sm flex items-start">
              <AlertCircle className="h-4 w-4 mr-2 mt-0.5 flex-shrink-0" />
              <p>You're not logged in. For best results, please <a href="/login" className="underline font-medium">login</a> before purchasing to save your gift card to your account.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default GiftCardCheckout;